import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Flex from "../../designLayouts/Flex";
import { FaSearch, FaShoppingCart, FaUser, FaTimes } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { setSearch } from "../../../redux/filtersSlice";
import Cart from "../../../pages/Cart/Cart";
import { toggleCart } from "../../../redux/shopSlice";
import { logout } from "../../../redux/authSlice";
import { resetCart } from "../../../redux/cartSlice";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const products = useSelector((state) => state.cartReducer.products);
  const isCartOpen = useSelector((state) => state.view.cart);
  const searchQuery = useSelector((state) => state.filters.search);
  const [searchValue, setSearchValue] = useState(searchQuery);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchValue(""); // Reset the state value
  };
  useEffect(() => {
    setSearchValue(searchQuery);
  }, [searchQuery]);

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      const query = searchValue;
      if (query && query.length < 3) {
        window.alert("Please enter at least 3 characters to search.");
      } else {
        dispatch(setSearch(query)); // Update Redux state when search is submitted
        setIsSearchVisible(false);
        if (location.pathname !== "/shop") {
          navigate("/shop"); // Navigate to shop page
        }
      }
    }
  };

  const toggleSearchDialog = () => setIsSearchVisible((prev) => !prev);

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  const handleLogout = () => {
    dispatch(logout({ dispatch }));
    dispatch(resetCart());
    navigate("/signin");
  };
  const isShopPath = location.pathname === "/shop";

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setTimeout(() => {
          setIsDropdownOpen(false);
        }, 100);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <div
      className={`w-full h-[40px] bg-primeColor z-50 border-b-[1px] border-b-gray-200 ${
        isShopPath ? "sticky top-0" : ""
      }`}
    >
      <nav className="h-full px-6 relative">
        <Flex className="flex items-center justify-between h-full">
          {/* Logo */}
          <Link to="/">
            <div className="text-white text-xl md:text-2xl font-semibold max-md:max-w-[100px]  break-words truncate">
              {sessionStorage.getItem("company_name")}
            </div>
          </Link>

          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center gap-6">
            <div className="relative w-[300px] h-[28px] text-base  bg-white flex items-center gap-2 justify-between px-3 rounded">
              <input
                className="flex-1 h-full outline-none placeholder:text-[#C4C4C4] placeholder:text-[11px]"
                type="text"
                onChange={handleSearchChange}
                value={searchValue}
                onKeyDown={handleSearchKeyPress}
                placeholder="Search your products here"
              />
              {searchValue && (
                <button
                  className="text-red-500 text-sm focus:outline-none"
                  onClick={handleClearSearch}
                >
                  <FaTimes />
                </button>
              )}
              <button
                className="text-primeColor text-sm focus:outline-none"
                onClick={() => dispatch(setSearch(searchValue))}
              >
                <FaSearch />
              </button>
            </div>
            {/* Cart Button */}
            <button
              onClick={() => dispatch(toggleCart())}
              className="relative focus:outline-none"
            >
              <FaShoppingCart size={18} />
              <span className="absolute top-3 -right-2 text-xs w-4 h-4 flex items-center justify-center rounded-full bg-white text-black">
                {products.length > 0 ? products.length : 0}
              </span>
            </button>

            {/* User Dropdown */}
            <div className="relative">
              <button
                onClick={toggleDropdown}
                className="flex items-center gap-2  font-medium focus:outline-none"
              >
                <span>{sessionStorage.getItem("aux_name")}</span>
                <FaUser size={20} color="white" />
              </button>
              {isDropdownOpen && (
                <div
                  className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded p-4 text-black z-50"
                  ref={dropdownRef}
                >
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded"
                    onClick={() => {
                      navigate("/orders");
                      toggleDropdown();
                    }}
                  >
                    Order History
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded"
                    onClick={() => {
                      navigate("/invoices");
                      toggleDropdown();
                    }}
                  >
                    Unpaid Invoices
                  </button>
                  <hr className="my-2" />
                  <button
                    className="w-full text-left px-4 py-2 text-red-500 hover:bg-gray-100 rounded"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="lg:hidden flex items-center gap-4">
            {/* Toggle Search Dialog Button */}
            <button
              className="text-white text-sm font-bold"
              onClick={toggleSearchDialog}
            >
              <FaSearch size={16} />
            </button>

            {/* Cart Button */}
            <button
              onClick={() => dispatch(toggleCart())}
              className="relative focus:outline-none"
            >
              <FaShoppingCart size={20} />
              <span className="absolute top-3 -right-2 text-xs w-4 h-4 flex items-center justify-center rounded-full bg-white text-black">
                {products.length > 0 ? products.length : 0}
              </span>
            </button>

            <div className="relative">
              <button
                onClick={toggleDropdown}
                className="flex items-center gap-2  font-medium focus:outline-none"
              >
                <span className="max-sm:max-w-[50px] break-words truncate text-xs">
                  {sessionStorage.getItem("aux_name")}
                </span>
                <FaUser size={20} color="white" />
              </button>
              {isDropdownOpen && (
                <div
                  className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded p-4 text-black z-50"
                  ref={dropdownRef}
                >
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded"
                    onClick={() => navigate("/orders")}
                  >
                    Order History
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded"
                    onClick={() => navigate("/invoices")}
                  >
                    Unpaid Invoices
                  </button>
                  <hr className="my-2" />
                  <button
                    className="w-full text-left px-4 py-2 text-red-500 hover:bg-gray-100 rounded"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </Flex>
      </nav>

      {/* Search Dialog */}
      {isSearchVisible && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
          onClick={toggleSearchDialog} // Close when clicking outside
        >
          <div
            className="relative w-[90%] max-w-[400px] flex flex-col gap-4 items-center"
            onClick={(e) => e.stopPropagation()} // Prevent close when clicking inside
          >
            {/* Search Input */}
            <div className="w-full relative bg-white h-[50px] flex items-center gap-2 px-4 rounded-xl shadow-xl">
              <input
                className="flex-1 h-full outline-none placeholder:text-[#C4C4C4] placeholder:text-[14px]"
                type="text"
                onChange={handleSearchChange}
                value={searchValue} // Bind the input value to the state
                onKeyDown={handleSearchKeyPress}
                placeholder="Search your products here"
              />
              {searchValue && (
                <button
                  className="text-red-500 text-sm focus:outline-none"
                  onClick={handleClearSearch}
                >
                  <FaTimes />
                </button>
              )}
              <button
                className="text-primeColor text-sm focus:outline-none"
                onClick={() => {
                  dispatch(setSearch(searchValue));
                  setIsSearchVisible(false);
                }}
              >
                <FaSearch />
              </button>
            </div>
          </div>
        </div>
      )}
      <Cart isOpen={isCartOpen} onClose={() => dispatch(toggleCart())} />
    </div>
  );
};

export default Header;
